'use client'
import React, {
  ChangeEventHandler,
  createContext,
  FormEventHandler,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { EventEmmiter } from '@/utils/EventEmmiter'

export const FormContext = createContext(new EventEmmiter())

type FormValues = {
  [key: string]: any
}
type FormProps = {
  onSubmit?: (values: FormValues) => void
  data?: { [key: string]: any }
  onChange?: (name: string, value: any) => void
  className?: string
  testId?: string
}
export const Form: FunctionComponent<PropsWithChildren<FormProps>> = ({
  children,
  onSubmit = () => {},
  data,
  onChange = () => {},
  className = '',
  testId = 'form',
}) => {
  const formRef = useRef<HTMLFormElement>(null)
  const eventBus = useRef<EventEmmiter>(new EventEmmiter())

  useEffect(() => {
    eventBus.current = new EventEmmiter()
  }, [])

  useEffect(() => {
    if (data) {
      if (formRef.current != null) {
        const { elements } = formRef.current
        Object.entries(data).forEach(([key, value]) => {
          const element = elements[key]
          if (element) {
            element.value = value
            eventBus.current.emit('change_' + key, value)
          }
        })
      }
    }
  }, [data])

  const submit: FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault()
      if (formRef.current != null) {
        const { elements } = formRef.current
        const values = Array.from(elements).reduce((data, element) => {
          // @TODO fix types
          // @ts-ignore
          const { name, type, value } = element
          if (!(element instanceof HTMLButtonElement)) {
            // @TODO fix types
            // @ts-ignore
            data[name] = type === 'checkbox' ? element.checked : value
          }
          return data
        }, {})
        onSubmit(values)
      }
    },
    [onSubmit]
  )

  const change: ChangeEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      const name = event.target.name
      const value = event.target.value
      onChange(name, value)
    },
    [onChange]
  )

  return (
    <FormContext.Provider value={eventBus.current}>
      <form
        ref={formRef}
        onSubmit={submit}
        onChange={change}
        className={className}
        data-testid={testId}
      >
        {children}
      </form>
    </FormContext.Provider>
  )
}
